/**
 * Column definitions for the Firm Contacts datatable.
 */

import { type ColumnDef } from "@tanstack/react-table";
import { Archive } from "lucide-react";
import { Badge } from "@/ui/primitives/badge";
import { cn } from "@/utils/misc";
import {
  CONTACT_TYPE_META,
  firmContactState,
  formatAbn,
  formatAcn,
  resolveFirmCategoryDisplay,
} from "./types";
import type { FirmContactCustomCategory, FirmContactRow } from "./types";

/**
 * Build the column set. A builder (rather than a static array) so the
 * Category cell can resolve user-defined custom categories (MOR-914) via
 * the org's live category list.
 */
export function buildFirmContactsColumns(
  customCategoriesById: ReadonlyMap<string, FirmContactCustomCategory>,
): Array<ColumnDef<FirmContactRow>> {
  return [
    {
      accessorKey: "displayName",
      header: "Name",
      meta: { label: "Name" },
      enableHiding: false,
      cell: ({ row }) => {
        const c = row.original;
        const TypeIcon = CONTACT_TYPE_META[c.contactType].icon;
        const isArchived = c.status === "archived";
        const linkedDisplay = c.linkedUser
          ? [c.linkedUser.firstName, c.linkedUser.lastName]
              .filter((s): s is string => Boolean(s))
              .join(" ") || c.linkedUser.email
          : null;
        const hasIdentifiers = c.abn || c.acn;
        return (
          <div className="flex min-w-0 items-start gap-2">
            <TypeIcon className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
            <div className="min-w-0">
              <div className="flex min-w-0 items-center gap-1.5">
                <span
                  className={cn(
                    "truncate text-sm font-medium",
                    isArchived
                      ? "text-muted-foreground line-through"
                      : "text-foreground",
                  )}
                >
                  {linkedDisplay ?? c.displayName}
                </span>
                {isArchived && (
                  <Badge
                    color="gray"
                    variant="soft"
                    size="sm"
                    className="gap-1"
                  >
                    <Archive className="h-3 w-3" />
                    Archived
                  </Badge>
                )}
              </div>
              {hasIdentifiers && (
                <div className="mt-0.5 flex items-center gap-2 text-[11px] text-muted-foreground/70">
                  {c.acn && (
                    <span className="font-mono tabular-nums">
                      ACN {formatAcn(c.acn)}
                    </span>
                  )}
                  {c.acn && c.abn && <span className="h-3 w-px bg-border" />}
                  {c.abn && (
                    <span className="font-mono tabular-nums">
                      ABN {formatAbn(c.abn)}
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>
        );
      },
    },
    {
      accessorKey: "category",
      header: "Category",
      meta: { label: "Category" },
      cell: ({ row }) => {
        const display = resolveFirmCategoryDisplay(
          row.original,
          customCategoriesById,
        );
        const Icon = display.icon;
        return (
          <Badge
            color={display.badgeColor}
            variant="soft"
            size="sm"
            className="gap-1 whitespace-nowrap"
          >
            <Icon className="h-3 w-3 shrink-0" />
            {display.shortLabel}
          </Badge>
        );
      },
    },
    {
      id: "contactType",
      header: "Type",
      meta: { label: "Type" },
      cell: ({ row }) => {
        const meta = CONTACT_TYPE_META[row.original.contactType];
        const color =
          row.original.contactType === "person"
            ? "blue"
            : row.original.contactType === "authority"
              ? "amber"
              : "gray";
        return (
          <Badge color={color} variant="soft" size="sm">
            {meta.label}
          </Badge>
        );
      },
    },
    {
      id: "firm",
      header: "Firm",
      meta: { label: "Firm" },
      // MOR-995 — the organisation a person nests under (e.g. a referrer's firm).
      accessorFn: (row) => row.parentDisplayName ?? "",
      cell: ({ row }) => {
        const name = row.original.parentDisplayName;
        if (!name) {
          return <span className="text-xs text-muted-foreground/50">—</span>;
        }
        return (
          <Badge
            color="gray"
            variant="soft"
            size="sm"
            className="whitespace-nowrap"
          >
            {name}
          </Badge>
        );
      },
    },
    {
      accessorKey: "email",
      header: "Email",
      meta: { label: "Email" },
      cell: ({ row }) => {
        const v = row.original.email;
        if (!v)
          return <span className="text-xs text-muted-foreground/50">—</span>;
        return (
          <span className="truncate text-xs text-muted-foreground" title={v}>
            {v}
          </span>
        );
      },
    },
    {
      accessorKey: "phone",
      header: "Phone",
      meta: { label: "Phone" },
      cell: ({ row }) => {
        const v = row.original.phone;
        if (!v)
          return <span className="text-xs text-muted-foreground/50">—</span>;
        return (
          <span className="font-mono text-xs tabular-nums text-muted-foreground">
            {v}
          </span>
        );
      },
    },
    {
      id: "state",
      header: "State",
      meta: { label: "State" },
      accessorFn: (row) => firmContactState(row) ?? "",
      cell: ({ row }) => {
        const state = firmContactState(row.original);
        if (!state) {
          return <span className="text-xs text-muted-foreground/50">—</span>;
        }
        return (
          <Badge
            color="gray"
            variant="soft"
            size="sm"
            className="whitespace-nowrap"
          >
            {state}
          </Badge>
        );
      },
    },
    {
      id: "addresses",
      header: "Addresses",
      meta: { label: "Addresses" },
      cell: ({ row }) => {
        const { addresses } = row.original;
        if (addresses.length === 0) {
          return <span className="text-xs text-muted-foreground/50">—</span>;
        }
        const first = addresses[0];
        const parts = [first.line1, first.suburb, first.state, first.postcode]
          .filter(Boolean)
          .join(", ");
        return (
          <div className="flex min-w-0 flex-col gap-0.5 text-xs text-muted-foreground">
            <span className="max-w-[200px] truncate">{parts || "Address"}</span>
            {addresses.length > 1 && (
              <span className="text-muted-foreground/60">
                +{addresses.length - 1} more
              </span>
            )}
          </div>
        );
      },
    },
    {
      accessorKey: "jobsUsing",
      header: "Jobs",
      meta: { label: "Jobs" },
      cell: ({ row }) => {
        const n = row.original.jobsUsing;
        return (
          <span className="text-sm tabular-nums text-muted-foreground">
            {n}
          </span>
        );
      },
    },
  ];
}
